import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '@core/components/base-component';
import {
  ESTIMATE_PROJECT_ITEMS,
  ESTIMATE_STATUS_ITEMS,
  ESTIMATE_TYPE_ITEMS,
  MY_PROJECTS_STORAGE_NAME
} from '@core/data/filter.config';
import { EnumItem } from '@core/models/enum-item.model';
import { EstimateTypeEnum } from '@enums/EstimateType';
import { StatusEnum } from '@enums/Status';
import { CreateEstimateQuery } from '@estimate/create-estimate/models/create-estimate-query.model';
import { Estimate } from '@interfaces/Estimate';
import { AppSettingsService } from '@shared/services/app-settings.service';
import { EstimateSettingsService } from '@shared/services/estimate-settings.service';
import { EstimateService } from '@shared/services/estimate.service';
import { LoadingService } from '@shared/services/loading.service';
import { ProjectService } from '@shared/services/project.service';
import { UserService } from '@shared/services/user.service';
import { Table } from 'primeng/table';
import { combineLatest } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'pop-estimate-list',
  templateUrl: './estimate-list.component.html',
  styleUrls: ['./estimate-list.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class EstimateListComponent extends BaseComponent implements OnInit {
  // for the list in the view, for each group, we will display the last revision's details

  private latestEstimates: Estimate[] = [];
  private projectCodes: string[] = [];

  public filterForm: UntypedFormGroup;
  public filterApplied = false;
  public hasCRM: boolean;
  public latestEstimatesUnfiltered: Estimate[] = [];
  public showFilters = false;
  public readonly statusItems = ESTIMATE_STATUS_ITEMS;
  public readonly typeItems = ESTIMATE_TYPE_ITEMS;
  public readonly projectItems = ESTIMATE_PROJECT_ITEMS;

  @ViewChild('dt', { static: true })
  private table: Table;

  public isCreateEstimateOpen: boolean;

  public constructor(
    private fb: UntypedFormBuilder,
    private estimateService: EstimateService,
    private projectService: ProjectService,
    private estimateSettingsService: EstimateSettingsService,
    private appSettingsService: AppSettingsService,
    private userService: UserService,
    private loadingService: LoadingService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    super();
  }

  public ngOnInit() {
    this.subscribe(
      this.activatedRoute.queryParams.subscribe((queryParams: CreateEstimateQuery) => {
        this.isCreateEstimateOpen = queryParams.newEstimateDialog === 'open';
      })
    );

    this.filterForm = this.fb.group({
      statuses: this.createGroupFromItems<StatusEnum>(this.statusItems),
      types: this.createGroupFromItems<EstimateTypeEnum>(this.typeItems),
      projects: this.createGroupFromItems<string>(this.projectItems)
    });
    this.subscribe(this.filterForm.valueChanges.subscribe(() => this.filterEstimates()));
    this.setupProjectsFilterForm();

    this.loadingService.setIsLoading(true);

    this.subscribe(
      combineLatest({
        estimateSettings: this.estimateSettingsService.getEstimateConfiguration(false),
        appSettings: this.appSettingsService.getAppSettings(),
        user: this.userService.getUser()
      })
        .pipe(
          switchMap(result => {
            this.hasCRM = result.appSettings.hasCRM;
            const restrictedCompanies = result.estimateSettings.restrictedCompanies.split(',');
            const userCode = result.user.resourceCode;

            return combineLatest({
              userProjectCodes: this.projectService.getProjectCodesByUserCode(userCode),
              latestEstimates: this.estimateService.getLatestEstimates(result.user, restrictedCompanies)
            });
          })
        )
        .subscribe(result => {
          if (result.latestEstimates) {
            this.latestEstimates = result.latestEstimates;
          }

          this.projectCodes = result.userProjectCodes;
          this.loadingService.setIsLoading(false);
          this.latestEstimatesUnfiltered = this.latestEstimates;
          this.filterEstimates();
        })
    );
  }

  private setupProjectsFilterForm(): void {
    this.filterForm.get('projects.MyProjects').patchValue(Boolean(localStorage.getItem(MY_PROJECTS_STORAGE_NAME)), {
      emitEvent: true
    });
    this.subscribe(
      this.filterForm
        .get('projects.MyProjects')
        .valueChanges.subscribe(filterProjects =>
          filterProjects
            ? localStorage.setItem(MY_PROJECTS_STORAGE_NAME, '1')
            : localStorage.removeItem(MY_PROJECTS_STORAGE_NAME)
        )
    );
  }

  private createGroupFromItems<T>(enumItems: EnumItem<T>[]): UntypedFormGroup {
    return this.fb.group(enumItems.reduce((group, item) => ({ ...group, [item.key as string]: !!item.selected }), {}));
  }

  private filterEstimates() {
    this.filterApplied = Object.values(this.filterForm.value).some(group => Object.values(group).some(Boolean));

    const statuses = this.filterForm.get('statuses').value as StatusEnum;
    const types = this.filterForm.get('types').value as EstimateTypeEnum;
    const projects = this.filterForm.get('projects').value as string;

    this.latestEstimatesUnfiltered = this.latestEstimates.filter(estimate => {
      if (Object.values(statuses).some(Boolean) && !statuses[estimate.status]) {
        return false;
      }
      if (Object.values(types).some(Boolean) && !types[estimate.estimateType]) {
        return false;
      }
      if (projects['MyProjects'] && !this.projectCodes.includes(estimate.projectCode)) {
        return false;
      }
      return true;
    });
  }

  public showFilterSidebar() {
    this.showFilters = !this.showFilters;
  }

  public clearFilters() {
    this.table.reset();
    this.filterForm.reset();
    this.filterApplied = false;
  }

  public async openNewEstimationDialog() {
    const queryParams: CreateEstimateQuery = { newEstimateDialog: 'open' };

    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: queryParams,
      queryParamsHandling: 'merge' // remove to replace all query params by provided
    });
  }

  public filterTable(table: Table, eventTarget: EventTarget) {
    const input = eventTarget as HTMLInputElement;
    table.filterGlobal(input.value, 'contains');
  }
}
